import { useCallback } from 'react';
import styled from 'styled-components';

import { BORDER_RADIUS } from '@front/common/src/components/selectable-card/constants';

import type { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Label from '@front/web-ui/src/components/label/label';
import Radio from '@front/web-ui/src/components/radio/radio';
import SelectableCard, { type SelectableCardProps } from '@front/web-ui/src/components/selectable-card/selectable-card';
import type { WithClassNameProps } from '@front/web-ui/src/components/types';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTextContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledRadioWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled(Label)`
  position: absolute;
  right: 0;
  top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: ${BORDER_RADIUS}px;
  border-bottom-right-radius: 0;
`;

const StyledFullWidthLabel = styled(Label)`
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  border-radius: ${BORDER_RADIUS}px ${BORDER_RADIUS}px 0 0;
  text-align: center;
`;

export type SelectablePriceCardProps = Pick<
  SelectableCardProps<MembershipOptIn>,
  'isSelected' | 'value' | 'onPress' | 'label' | 'children'
> &
  WithClassNameProps & {
    hasFullWidthLabel?: boolean;
    title: string;
  };

const SelectablePriceCard = ({
  children,
  className,
  hasFullWidthLabel,
  label,
  isSelected,
  onPress,
  title,
  value,
}: SelectablePriceCardProps) => {
  const onCardPress = useCallback(() => {
    onPress(value);
  }, [onPress, value]);

  return (
    <Wrapper>
      {!!label && !hasFullWidthLabel && (
        <StyledLabel backgroundColor='pink120' size='xxs'>
          {label}
        </StyledLabel>
      )}
      {!!label && hasFullWidthLabel && (
        <StyledFullWidthLabel backgroundColor='pink120' size='xs'>
          {label}
        </StyledFullWidthLabel>
      )}
      <SelectableCard<MembershipOptIn>
        hasFullWidth
        isSelected={isSelected}
        label={title}
        onPress={onCardPress}
        role='radio'
        value={value}
        className={className}
      >
        <StyledTextContent>
          <StyledRadioWrapper>
            <Radio
              value={value}
              name={`selectable-price-card-name-${value}`}
              id={`selectable-price-card-id-${value}`}
              checked={!!isSelected}
              onChange={onCardPress}
            />
            <Text.MDBold>{title}</Text.MDBold>
          </StyledRadioWrapper>
          {children}
        </StyledTextContent>
      </SelectableCard>
    </Wrapper>
  );
};

export default SelectablePriceCard;
